import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import practiceService from '../../services/practiceService';
import {
  normalizePracticeMonitoringTypes
} from '../../utils/practiceMonitoringUtils';
import BloodPressure from './BloodPressure/BloodPressure';
import DailyTrendChart from './BloodPressure/Graphs/DailyTrendChart';
import MeanChart from './BloodPressure/Graphs/MeanChart';
import BloodGlucose from './BloodGlucose/BloodGlucose';
import DayChart from './BloodGlucose/Graphs/DayChart';
import MeanChartBG from './BloodGlucose/Graphs/MeanChart';
import Weight from './Weight/Weight';
import WeightGraphs from './Weight/Graphs/WeightGraphs';
import MeasurementControlsBar from './MeasurementControlsBar';
import './MeasurementShell.css';

class MeasurementErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unexpected rendering error' };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[MeasurementData] render error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, margin: 12, border: '1px solid #f0b3b3', borderRadius: 8, background: '#fff7f7' }}>
          <strong>Unable to render measurement screen.</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>{this.state.errorMessage}</div>
          <button type="button" onClick={this.handleRetry} style={{ marginTop: 10 }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const getActiveVitalFromPath = (pathname = '') => {
  if (pathname.includes('/blood-glucose')) return 'blood-glucose';
  if (pathname.includes('/weight')) return 'weight';
  return 'blood-pressure';
};

const isChartPath = (pathname = '') => /\/(blood-pressure|blood-glucose|weight)\/chart/.test(pathname);

const MeasurementVitalPane = ({ vitalKey, activeVital, mountedVitals, children }) => {
  if (!mountedVitals.has(vitalKey)) return null;
  const isActive = activeVital === vitalKey;
  return (
    <div
      className="measurement-vital-pane"
      hidden={!isActive}
      aria-hidden={!isActive}
    >
      {children}
    </div>
  );
};

const MeasurementData = () => {
  const { practiceId, patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [practiceIsLocked, setPracticeIsLocked] = useState(false);
  const [practiceMonitoringTypes, setPracticeMonitoringTypes] = useState(null);

  const activeVital = useMemo(() => getActiveVitalFromPath(location.pathname), [location.pathname]);
  const onChart = useMemo(() => isChartPath(location.pathname), [location.pathname]);

  const [mountedVitals, setMountedVitals] = useState(() => new Set([getActiveVitalFromPath(location.pathname)]));

  useEffect(() => {
    if (onChart) return;
    const hasVital = /\/(blood-pressure|blood-glucose|weight)(\/|$)/.test(location.pathname);
    if (!hasVital) {
      navigate('blood-pressure', { replace: true, state: location.state });
    }
  }, [location.pathname, location.state, navigate, onChart]);

  useEffect(() => {
    if (onChart) return;
    setMountedVitals((prev) => {
      if (prev.has(activeVital)) return prev;
      const next = new Set(prev);
      next.add(activeVital);
      return next;
    });
  }, [activeVital, onChart]);

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    practiceService
      .getPractice(practiceId)
      .then((res) => {
        if (cancelled) return;
        const s = res?.data?.data?.status;
        const locked =
          s === 4 ||
          String(s).trim() === '4' ||
          String(s).toLowerCase() === 'locked' ||
          String(s).toLowerCase() === 'lost';
        setPracticeIsLocked(Boolean(locked));
        setPracticeMonitoringTypes(
          normalizePracticeMonitoringTypes(res?.data?.data?.selected_monitoring_types)
        );
      })
      .catch(() => {
        if (!cancelled) setPracticeIsLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  return (
    <MeasurementErrorBoundary>
      <div className={`blood-pressure-page measurement-vitals-shell${onChart ? ' measurement-vitals-shell--chart' : ''}`}>
        {practiceIsLocked && (
          <div className="practice-locked-measurement-banner" role="status">
            This practice is locked. Historical readings remain available for up to 30 days. New measurements and
            follow-ups are not allowed.
          </div>
        )}
        <MeasurementControlsBar
          practiceId={practiceId}
          patientId={patientId}
          practiceIsLocked={practiceIsLocked}
          activeVital={activeVital}
          vitalsMode={onChart ? 'chart' : 'measurement'}
        >
          {onChart ? (
            <Routes>
              <Route
                path="blood-pressure/chart"
                element={<DailyTrendChart practiceId={practiceId} patientId={patientId} />}
              />
              <Route
                path="blood-pressure/chart/mean"
                element={<MeanChart practiceId={practiceId} patientId={patientId} />}
              />
              <Route
                path="blood-glucose/chart"
                element={<DayChart practiceId={practiceId} patientId={patientId} />}
              />
              <Route
                path="blood-glucose/chart/mean"
                element={<MeanChartBG practiceId={practiceId} patientId={patientId} />}
              />
              <Route path="weight/chart" element={<WeightGraphs />} />
              <Route path="*" element={<Navigate to="../blood-pressure" replace />} />
            </Routes>
          ) : (
            <>
              <MeasurementVitalPane vitalKey="blood-pressure" activeVital={activeVital} mountedVitals={mountedVitals}>
                <BloodPressure practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} practiceMonitoringTypes={practiceMonitoringTypes} />
              </MeasurementVitalPane>
              <MeasurementVitalPane vitalKey="blood-glucose" activeVital={activeVital} mountedVitals={mountedVitals}>
                <BloodGlucose practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} practiceMonitoringTypes={practiceMonitoringTypes} />
              </MeasurementVitalPane>
              <MeasurementVitalPane vitalKey="weight" activeVital={activeVital} mountedVitals={mountedVitals}>
                <Weight practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} practiceMonitoringTypes={practiceMonitoringTypes} />
              </MeasurementVitalPane>
            </>
          )}
        </MeasurementControlsBar>
      </div>
    </MeasurementErrorBoundary>
  );
};

export default MeasurementData;
